import React from 'react';
import { Plus } from '../../bravosIcons';
import LibrarySelectionBar from '../library/components/LibrarySelectionBar';
import type { LibraryTab } from '../hooks/useLibraryLoader';

export type LibraryPrimaryCta = { label: string; onClick: () => void };

export type LibrarySectionShellProps = {
  variant: 'mobile' | 'desktop';
  activeTab: LibraryTab;
  onSelectTab: (tab: LibraryTab) => void;
  libTabCount: (tab: LibraryTab) => string;
  loadingLibrary: boolean;
  primaryCta: LibraryPrimaryCta;
  /** Mobile-only: legacy Create New dropdown */
  mobileCreateMenu?: {
    isOpen: boolean;
    onToggle: () => void;
    menuRef: React.RefObject<HTMLDivElement>;
    onCreateExercise: () => void;
    onCreateWorkout: () => void;
    onCreateWorkoutPlan: () => void;
  };
  /** Mobile-only: legacy Actions dropdowns when rows selected */
  mobileBulkActions?: React.ReactNode;
  /** Per-tab search row (mobile layout) */
  mobileSearchSlot?: React.ReactNode;
  /** Desktop: selection bar + primary CTA region */
  desktopSelection?: {
    selectedWorkoutsCount: number;
    selectedWorkoutPlansCount: number;
    selectedExercisesCount: number;
    onDuplicateWorkouts: () => void;
    onDeleteWorkouts: () => void;
    onCloseWorkouts: () => void;
    workoutDuplicateDisabled: boolean;
    onDuplicatePlans: () => void;
    onDeletePlans: () => void;
    onClosePlans: () => void;
    planDuplicateDisabled: boolean;
    onDuplicateExercises: () => void;
    onDeleteExercises: () => void;
    onCloseExercises: () => void;
    exerciseDuplicateDisabled: boolean;
  };
  children: React.ReactNode;
};

const libraryTabButtonClass = (activeTab: LibraryTab, tab: LibraryTab) =>
  `pb-3 pt-1 border-b-2 font-medium text-sm transition-colors -mb-px ${
    activeTab === tab
      ? 'border-slate-900 text-slate-900 dark:border-theme-off-white dark:text-theme-off-white'
      : 'border-transparent text-slate-500 hover:text-slate-900 dark:text-theme-off-white/60 dark:hover:text-theme-off-white'
  }`;

const libraryCreateButtonClass =
  'inline-flex h-11 items-center rounded-2xl bg-slate-900 px-5 text-sm font-medium text-white transition-colors hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:ring-offset-2 dark:bg-accent-lime dark:text-theme-bg-dark dark:hover:opacity-90 dark:focus:ring-accent-lime/40';
const libraryCreateMenuClass =
  'absolute left-0 top-full z-[60] mt-2 w-full min-w-full rounded-2xl border border-slate-200 bg-white p-2 shadow-[0_16px_40px_rgba(15,23,42,0.12)] dark:border-white/10 dark:bg-theme-surface';

function LibraryMobileShell({
  activeTab,
  onSelectTab,
  mobileCreateMenu,
  mobileBulkActions,
  mobileSearchSlot,
  loadingLibrary,
  children,
}: LibrarySectionShellProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-6" style={{ position: 'relative', zIndex: 50, overflow: 'visible' }}>
        <div className="border-b border-theme-border/70 dark:border-white/10 -mb-px">
          <nav className="-mb-px flex gap-8">
            <button type="button" onClick={() => onSelectTab('workoutPlans')} className={libraryTabButtonClass(activeTab, 'workoutPlans')}>
              Workout Plans
            </button>
            <button type="button" onClick={() => onSelectTab('workouts')} className={libraryTabButtonClass(activeTab, 'workouts')}>
              Workouts
            </button>
            <button type="button" onClick={() => onSelectTab('exercises')} className={libraryTabButtonClass(activeTab, 'exercises')}>
              Exercises
            </button>
          </nav>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 pb-3">
          {mobileCreateMenu && (
            <div className="relative" ref={mobileCreateMenu.menuRef}>
              <button
                type="button"
                onClick={mobileCreateMenu.onToggle}
                className={libraryCreateButtonClass}
                aria-label="Create new"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create New
              </button>
              {mobileCreateMenu.isOpen && (
                <>
                  <div className="fixed inset-0 z-[59]" onClick={() => mobileCreateMenu.onToggle()} aria-hidden="true" />
                  <div className={libraryCreateMenuClass}>
                    <button
                      type="button"
                      onClick={mobileCreateMenu.onCreateExercise}
                      className="block w-full rounded-xl px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-theme-off-white dark:hover:bg-white/10"
                    >
                      Exercise
                    </button>
                    <button
                      type="button"
                      onClick={mobileCreateMenu.onCreateWorkout}
                      className="block w-full rounded-xl px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-theme-off-white dark:hover:bg-white/10"
                    >
                      Workout
                    </button>
                    <button
                      type="button"
                      onClick={mobileCreateMenu.onCreateWorkoutPlan}
                      className="block w-full rounded-xl px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 dark:text-theme-off-white dark:hover:bg-white/10"
                    >
                      Workout Plan
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
          {mobileBulkActions}
        </div>
      </div>

      {mobileSearchSlot}

      <div style={{ overflow: 'visible', position: 'relative' }}>
        {loadingLibrary && activeTab !== 'exercises' ? (
          <div className="flex items-center justify-center py-12">
            <div
              className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
                activeTab === 'workouts'
                  ? 'border-blue-600'
                  : activeTab === 'workoutPlans'
                    ? 'border-purple-600'
                    : 'border-emerald-600'
              }`}
            />
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function LibraryDesktopShell({
  activeTab,
  onSelectTab,
  libTabCount,
  primaryCta,
  desktopSelection,
  children,
}: LibrarySectionShellProps) {
  const showSelection =
    desktopSelection &&
  ((activeTab === 'workouts' && desktopSelection.selectedWorkoutsCount > 0) ||
    (activeTab === 'workoutPlans' && desktopSelection.selectedWorkoutPlansCount > 0) ||
    (activeTab === 'exercises' && desktopSelection.selectedExercisesCount > 0));

  return (
    <div className="tr-library-shell" role="region" aria-label="Library">
      <div className="lib-page-header">
        <h1 className="lib-page-title">
          Library<em>.</em>
        </h1>
      </div>

      <div className="lib-tabs-row">
        <div className="lib-tabs" role="tablist" aria-label="Library tabs">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'workoutPlans'}
            className={`lib-tab ${activeTab === 'workoutPlans' ? 'is-active' : ''}`}
            onClick={() => onSelectTab('workoutPlans')}
          >
            Workout Plans
            <span className="lib-tab-count lib-tnum">{libTabCount('workoutPlans')}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'workouts'}
            className={`lib-tab ${activeTab === 'workouts' ? 'is-active' : ''}`}
            onClick={() => onSelectTab('workouts')}
          >
            Workouts
            <span className="lib-tab-count lib-tnum">{libTabCount('workouts')}</span>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'exercises'}
            className={`lib-tab ${activeTab === 'exercises' ? 'is-active' : ''}`}
            onClick={() => onSelectTab('exercises')}
          >
            Exercises
            <span className="lib-tab-count lib-tnum">{libTabCount('exercises')}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {!showSelection && (
            <button type="button" className="lib-cta-primary" onClick={primaryCta.onClick} aria-label={primaryCta.label}>
              <Plus />
              {primaryCta.label}
            </button>
          )}

          {desktopSelection && activeTab === 'workouts' && desktopSelection.selectedWorkoutsCount > 0 && (
            <LibrarySelectionBar
              count={desktopSelection.selectedWorkoutsCount}
              itemNoun="workout"
              onDuplicate={desktopSelection.onDuplicateWorkouts}
              onDelete={desktopSelection.onDeleteWorkouts}
              onClose={desktopSelection.onCloseWorkouts}
              duplicateDisabled={desktopSelection.workoutDuplicateDisabled}
            />
          )}
          {desktopSelection && activeTab === 'workoutPlans' && desktopSelection.selectedWorkoutPlansCount > 0 && (
            <LibrarySelectionBar
              count={desktopSelection.selectedWorkoutPlansCount}
              itemNoun="plan"
              onDuplicate={desktopSelection.onDuplicatePlans}
              onDelete={desktopSelection.onDeletePlans}
              onClose={desktopSelection.onClosePlans}
              duplicateDisabled={desktopSelection.planDuplicateDisabled}
            />
          )}
          {desktopSelection && activeTab === 'exercises' && desktopSelection.selectedExercisesCount > 0 && (
            <LibrarySelectionBar
              count={desktopSelection.selectedExercisesCount}
              itemNoun="exercise"
              onDuplicate={desktopSelection.onDuplicateExercises}
              onDelete={desktopSelection.onDeleteExercises}
              onClose={desktopSelection.onCloseExercises}
              duplicateDisabled={desktopSelection.exerciseDuplicateDisabled}
            />
          )}
        </div>
      </div>

      {children}
    </div>
  );
}

/** Library shell — tabs, counts, create CTA, bulk bar, loading gate (R5). Tab tables render as children. */
export function LibrarySection(props: LibrarySectionShellProps) {
  if (props.variant === 'mobile') {
    return <LibraryMobileShell {...props} />;
  }
  return <LibraryDesktopShell {...props} />;
}
